import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { useEffect } from 'react';
import i18n from './i18n/i18n';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { growthbook } from './lib/growthbookClient';

import Home from './Home';
import AuthModal from './components/shared/AuthModal';
import Summary from './components/Summary';
import CookieBanner from './components/CookieBanner';
import useGTM from './hooks/useGtm';
import { GrowthBookProvider as CustomGrowthBookProvider } from './contexts/GrowthBookContext';
import { ConsentProvider } from './contexts/ConsentContext';
import { resolvePostSignupRedirect } from './utils/postSignupRedirect';
import { trackRegistrationCompleted } from './utils/analytics';

const supported = ['en', 'fr', 'de', 'es', 'it', 'ja', 'ar', 'id', 'ko', 'nl', 'pl', 'pt', 'ru', 'tr', 'zh'];

const browserLang = (() => {
  const lang = navigator.language.split('-')[0];
  return supported.includes(lang) ? lang : 'en';
})();

function SummaryPage() {
  return <Summary />;
}

function SignupModalPage() {
  const navigate = useNavigate();
  return (
    <AuthModal
      isOpen={true}
      onClose={() => navigate(-1)}
      selectedStyle="Realistic"
    />
  );
}

function LanguageWrapper() {
  useGTM();

  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage(browserLang);
      return;
    }

    if (supported.includes(lang)) {
      i18n.changeLanguage(lang);
      if (
        lang === browserLang &&
        location.pathname.startsWith(`/${browserLang}`)
      ) {
        const cleanPath =
          location.pathname.replace(new RegExp(`^/${browserLang}`), '') || '/';
        navigate(cleanPath, { replace: true });
      }
      if (
        lang === 'en' &&
        browserLang === 'en' &&
        location.pathname.startsWith('/en')
      ) {
        const cleanPath = location.pathname.replace(/^\/en/, '') || '/';
        navigate(cleanPath, { replace: true });
      }
    } else {
      navigate('/', { replace: true });
    }
  }, [lang, location.pathname, navigate]);

  return <Outlet />;
}

export default function Root() {
  // Only redirect on Google OAuth resumption — i.e. the user just came back
  // from an OAuth round-trip and signup completed. We deliberately do NOT
  // auto-redirect for stale `isCompleted` flags from previous sessions; users
  // who already signed up should still be able to see the lander.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const canRedirect = urlParams.get('redirect') !== 'false';

    const inProgress = localStorage.getItem('googleAuthInProgress');
    const startTime = parseInt(localStorage.getItem('googleAuthStartTime'), 10);

    if (!inProgress || !startTime) return;

    const timeElapsed = Date.now() - startTime;
    const oauthCompleted = timeElapsed >= 3 * 60 * 1000;

    if (oauthCompleted) {
      localStorage.setItem('isCompleted', true);
      localStorage.removeItem('googleAuthCancelled');
      trackRegistrationCompleted();
    } else {
      localStorage.setItem('googleAuthCancelled', 'true');
    }

    localStorage.removeItem('googleAuthInProgress');
    localStorage.removeItem('googleAuthStartTime');

    if (oauthCompleted && canRedirect) {
      window.location.href = resolvePostSignupRedirect();
    }
  }, []);

  return (
    <ConsentProvider>
      <GrowthBookProvider growthbook={growthbook}>
        <CustomGrowthBookProvider>
          <Router basename={process.env.PUBLIC_URL || '/'}>
            <Routes>
              <Route path="/" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              <Route path="/:lang" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </CustomGrowthBookProvider>
      </GrowthBookProvider>
      <CookieBanner />
    </ConsentProvider>
  );
}
